import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef, useState } from "react";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useTranslation } from "react-i18next";
import ErrorToast from '../components/ErrorToast';
import ImplantCard from '../components/ImplantCard';
import { BarcodeIcon } from '../components/LabelSearch';
import { BrandSelector, CountrySelector, FeatureSelector } from '../components/Selectors';
import Spinner from '../components/Spinner';
import YearSlider from '../components/YearSlider';
import { getModels, labelSearch } from '../services';
import { context } from '../utils';


const currentYear = new Date().getFullYear();

const RESULTS_PER_PAGE = 21;

const SearchView = () => {

    const yearMin = 1970;
    const yearMax = currentYear;

    const [filters, setFilters] = useState({
        year_min: yearMin,
        year_max: yearMax
    });

    const [implants, setImplants] = useState([]);
    const [page, setPage] = useState(1);
    const [countAll, setCountAll] = useState();

    const [isLoading, setIsLoading] = useState(true);

    const [showFilters, setShowFilters] = useState(!context.is_mobile);

    const [errors, setErrors] = useState(null);

    const onChange = async (filters) => {


        setIsLoading(true);

        let data;
        try {
            data = await getModels({
                ...filters,
                page_size: RESULTS_PER_PAGE
            });
        }
        catch (e) {
            setIsLoading(false);
            setErrors({ [t("Search")]: t("An error occured, please contact us.") });
        }

        if (data && data.results) {
            setImplants(data.results);

            setCountAll(data.count);
        }
        setPage(1);
        setIsLoading(false);
    };

    const debounceOnChange = useCallback(debounce(onChange, 400), []);



    const setField = async (name, value) => {
        let newFilters;

        if (name === "year") {
            newFilters = {
                ...filters,
                year_min: value[0],
                year_max: value[1]
            };
        } else {
            newFilters = {
                ...filters,
                [name]: value
            };
        }


        setFilters(newFilters);


        if (newFilters !== filters) {

            if (newFilters.keyword !== filters.keyword || newFilters.year_min !== filters.year_min || newFilters.year_max !== filters.year_max) {
                debounceOnChange(newFilters);
            } else {
                onChange(newFilters);
            }

        }
    };

    const numPages = Math.ceil(countAll / RESULTS_PER_PAGE);

    const isLastPage = page === numPages;

    useEffect(() => {
        onChange({});
    }, []);

    const onImplantClick = implant => {
        window.location.href = ImplantDetailUrl(implant);
    };

    const onLoadMoreClick = async () => {
        setPage(prev => prev + 1);

        let data;
        try {
            data = await getModels({
                ...filters,
                page: page + 1,
                page_size: RESULTS_PER_PAGE,
            });
        }
        catch (e) {
            setIsLoading(false);
            setErrors({ [t("Search")]: t("An error occured, please contact us.") });
        }


        if (data && data.results) {
            setImplants([
                ...implants,
                ...data.results
            ]);
        }
    };

    const toggleShowFilters = () => {
        setShowFilters(prev => !prev);
    };

    const ImplantDetailUrl = implant => `/${context.current_language}/${t('dental-implants')}/${implant.brand_slug}/${implant.slug}`;

    const labelInputFile = useRef(null);

    const onLabelChange = async (event) => {
        const file = Object(event.currentTarget.files)[0];
        setIsLoading(true);
        let data;
        try {
            data = await labelSearch(file);
        } catch (e) {
            setIsLoading(false);
            setErrors({ [t("implant label")]: t("An error occured, try searching manually") });
        }

        if (data && data.implant_models) {
            setImplants(data.implant_models);

            setCountAll(data.implant_models.length);
        }
        setPage(1);
        setIsLoading(false);
    };


    const { t } = useTranslation();
    return <div className="my-container container" style={{ padding: "0", overflow: "visible" }}>
        <div className="row">
            <div className="col-sm-3 search-filters">
                <div action="" method="get" className="form" id="filter-form">
                    {context.is_mobile ?
                        <button className="btn btn-light advanced-toggle" type="button" onClick={toggleShowFilters}>
                            <i className="fas fa-filter"></i>
                        </button>
                        : ''}
                    <FilterContainer name={t("Keyword")} className="keyword-container">
                        <input type="text" name="query" className="form-control" placeholder={t("Implant model, brand, reference") + '...'} value={filters.keyword} onChange={e => setField("keyword", e.target.value)} />
                    </FilterContainer>

                    {showFilters ? <>

                        <FilterContainer name={t("Brand")}>
                            <BrandSelector isMulti value={filters.brand} onChange={option => setField("brand", option.map(x => x.value))} isSearchable={true} />
                        </FilterContainer>



                        <FilterContainer name={t("Type")}>
                            <FeatureSelector feature="kind" isMulti value={filters.kind} onChange={option => setField("kind", option.map(x => x.value))} />
                        </FilterContainer>
                        <FilterContainer name={t("Level")}>
                            <FeatureSelector feature="level" isMulti value={filters.level} onChange={option => setField("level", option.map(x => x.value))} />
                        </FilterContainer>


                        <FilterContainer name={t("Connection Type")}>
                            <FeatureSelector feature="connection_type" isMulti value={filters.connection_type} onChange={option => setField("connection_type", option.map(x => x.value))} />
                        </FilterContainer>


                        <FilterContainer name={t("Connection Feature")}>
                            <FeatureSelector feature="connection_feature" isMulti value={filters.connection_feature} onChange={option => setField("connection_feature", option.map(x => x.value))} />
                        </FilterContainer>

                        <FilterContainer name={t("Head Shape")}>
                            <FeatureSelector feature="head_shape" isMulti value={filters.head_shape} onChange={option => setField("head_shape", option.map(x => x.value))} />
                        </FilterContainer>


                        <FilterContainer name={t("Head Microthreads")}>
                            <FeatureSelector feature="head_microthreaded" isMulti value={filters.head_microthreaded} onChange={option => setField("head_microthreaded", option.map(x => x.value))} />
                        </FilterContainer>

                        <FilterContainer name={t("Body Shape")}>
                            <FeatureSelector feature="body_shape" isMulti value={filters.body_shape} onChange={option => setField("body_shape", option.map(x => x.value))} />
                        </FilterContainer>

                        <FilterContainer name={t("Body Threads")}>
                            <FeatureSelector feature="body_threads" isMulti value={filters.body_threads} onChange={option => setField("body_threads", option.map(x => x.value))} />
                        </FilterContainer>

                        <FilterContainer name={t("Apex Shape")}>
                            <FeatureSelector feature="apex_shape" isMulti value={filters.apex_shape} onChange={option => setField("apex_shape", option.map(x => x.value))} />
                        </FilterContainer>

                        <FilterContainer name={t("Apex Hole")}>
                            <FeatureSelector feature="apex_hole" isMulti value={filters.apex_hole} onChange={option => setField("apex_hole", option.map(x => x.value))} />
                        </FilterContainer>

                        <FilterContainer name={t("Apex Grooves")}>
                            <FeatureSelector feature="apex_grooves" isMulti value={filters.apex_grooves} onChange={option => setField("apex_grooves", option.map(x => x.value))} />
                        </FilterContainer>


                        <FilterContainer name={t("Connection Shape")} help={"Anti-rotational shape of the abutment-implant interface (if visible)"}>
                            <FeatureSelector feature="connection_shape" isMulti value={filters.connection_shape} onChange={option => setField("connection_shape", option.map(x => x.value))} />
                        </FilterContainer>

                        <FilterContainer name={t("Screwdriver Shape")}>
                            <FeatureSelector feature="screwdriver_shape" isMulti value={filters.screwdriver_shape} onChange={option => setField("screwdriver_shape", option.map(x => x.value))} />
                        </FilterContainer>


                        <FilterContainer name={t("Country")} help={t("Where implant is/was distributed")}>
                            <CountrySelector isMulti value={filters.distribution_country} onChange={option => setField("distribution_country", option.map(x => x.value))} isSearchable={true} />
                        </FilterContainer>


                        <FilterContainer
                            name={<>{t("Year")}<label id="year_range_label" className="text-muted">: {filters.year_min} - {filters.year_max}</label></>}
                            help={t("When implant was first commercialized (if unknown, still appears)")}
                        >
                            <YearSlider style={{ margin: "0 5px", width: "auto" }} range min={yearMin} max={yearMax} value={[filters.year_min, filters.year_max]} onChange={value => setField("year", value)} />
                        </FilterContainer>

                        <FilterContainer
                            name={t("Implant Label")}
                            help={<>{t('Take a picture of the label, it usually looks like this:')}<br /><img src={`${context.static_url}img/sample_label.png`} className="img-responsive" style={{ height: 50, backgroundColor: "white" }} /></>}
                            style={{ marginTop: 20 }}
                        >
                            <label className="btn btn-light btn-sm rounded-pill btn-block" style={{ fontSize: 12 }}>
                                {t("Submit implant label picture")}
                                {/* <i className="fas fa-camera"></i> */}

                                <BarcodeIcon style={{ marginLeft: 5 }} /> <input type="file" ref={labelInputFile} hidden onChange={onLabelChange} />
                            </label>
                        </FilterContainer>

                    </> : ''}
                    {/*
                    <button className="btn btn-primary btn-block" style={{ marginTop: "15px" }} type="submit">{t('Filter')}</button> */}
                </div>
            </div>
            <div className="col-sm-9 search-results container">
                <div className="row" style={{ margin: "0" }}>
                    <div className="col-md-12"><OneLineIdentificationCard /></div>
                </div>
                {!isLoading ?

                    implants && implants.length ?
                        <>< div className="row" style={{ margin: "0" }}>
                            {implants.map((implant, idx) => {
                                return <div key={idx} className="col-6 col-md-4"><ImplantCard implant={implant} /></div>;
                            })}
                        </div>
                            {!isLastPage ?
                                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                                    <button className="btn btn-lg btn-light rounded-pill" onClick={onLoadMoreClick}>{t('Load more')}</button>
                                </div> : ''}
                        </>

                        : <div className="text-muted empty">
                            <div>
                                <span className="oi oi-magnifying-glass"></span>
                                <p>{t("No Results")}</p>
                            </div>
                        </div>

                    : <Spinner style={{ minHeight: "70vh", alignItems: "center" }} />}
                <div className="container py-5" style={{ textAlign: "center" }}>
                    <IdentificationCard />
                </div>
            </div>
        </div >
        <ErrorToast errors={errors} />
    </div >;
};

const FilterContainer = ({ name, help, children, className, ...rest }) => {
    return <div className={"form-group " + className} {...rest}>
        <label htmlFor="id_query">{name}</label>
        {help ?
            <OverlayTrigger
                placement={"bottom"}
                overlay={
                    <Tooltip style={{ textAlign: "left!important" }}>
                        {help}
                    </Tooltip>
                }
            >
                <a className="pull-right text-muted info-icon"><i className="fas fa-info-circle"></i></a>
            </OverlayTrigger> : ""}

        {children}
    </div>;

};

export default SearchView;


const IdentificationCard = () => {
    const { t } = useTranslation();

    return <div className="card flex-row flex-wrap cta-card identification-card ">
        <div className="card-header border-0">
            <div className="img-container" style={{ backgroundImage: `url(${context.static_url}img/dental-implant-identification.png)` }} >
            </div>
        </div>
        <div className="card-block px-2">
            <p className="card-title">
                {t("Can't find your implant?")}
                <br /><small className="text-muted">{t("Try our AI-based identification service")}</small>
            </p>
            <a href={`${context.urls.identification}?from=search`} className="btn btn-primary btn-light stretched-link" spellcheck="false">
                {t("Submit a case")} {" "}<small class="fa fa-chevron-right"></small></a>
        </div>
    </div>;
};

const OneLineIdentificationCard = () => {
    const { t } = useTranslation();

    return <div className="card flex-row flex-wrap cta-card identification-card identification-card-one-line">
        <div className="card-header border-0">
            <div className="img-container" style={{ backgroundImage: `url(${context.static_url}img/dental-implant-identification.png)` }} >
            </div>
        </div>
        <div className="card-block px-2">
            <p className="card-title">
                {t("Can't find your implant?")} {t("Try our AI-based identification service")}.
            </p>
            <a href={`${context.urls.identification}?from=search`} className="btn btn-primary btn-light btn-sm stretched-link" spellcheck="false">
                {t("Submit a case")} {" "}<small class="fa fa-chevron-right"></small></a>
        </div>
    </div>;
};